import { useMemo, useState } from 'react'
import {
  AlertIcon,
  CheckIcon,
  CheckboxRow,
  DataSurface,
  Note,
  Pagination,
  StatusPill,
  VoiceIcon,
  toneIcon,
  usePagination,
} from '../../../shared/components'
import { useResource } from '../../../shared/hooks/useResource'
import {
  createBroadcast,
  getBroadcasts,
  getSchools,
  withdrawBroadcast,
  type BroadcastView,
  type SchoolSummary,
} from '../../../shared/api/platform'
import type { StaffAuth } from '../../../shared/api/teaching'
import { describePlatformError } from './copy'
import {
  broadcastDraftIsValid,
  describeAudience,
  toggleRole,
  validateBroadcastDraft,
  audienceLabel,
  BODY_MAX,
  BROADCAST_AUDIENCES,
  TITLE_MAX,
  type BroadcastDraft,
} from './broadcastModel'
import type { Lang } from '../../../shared/i18n'

/**
 * Change 19 §4 — *"while broadcasting messages, allow Super Admin to select the
 * target audience — Parents, Teachers, School Admins, or specific groups."*
 *
 * 🔴 THIS SCREEN USED TO CALL `POST /school/announcements`, WHICH BARS
 * SUPER_ADMIN AT THE CLASS LEVEL. Every post from here was a 403 with a compose
 * box in front of it: there was no audience picker because there was no
 * platform-shaped route to pick an audience *for*. `POST /platform/broadcasts`
 * is that route, and this is the form for it.
 *
 * 🔴 THE SENTENCE ABOVE THE BUTTON IS NOT DECORATION. An empty school picker
 * means EVERY SCHOOL — the opposite of what an empty role list means — and
 * without a line that says so out loud, "I did not choose any schools" and "this
 * goes to all of them" are the same screen.
 *
 * ⚠️ NOTHING IS SENT, AND THE FORM SAYS SO. No outbox, no WhatsApp template, no
 * phone number touched: a broadcast is a strip at the top of a dashboard the
 * next time somebody opens one. Copy that implied delivery would be the most
 * expensive kind of wrong here — an operator believing a closure notice reached
 * four hundred phones.
 */
export interface PlatformBroadcastScreenProps {
  lang?: Lang
  auth?: StaffAuth
}

const EMPTY: BroadcastDraft = { title: '', body: '', roles: [], schoolIds: [] }

export function PlatformBroadcastScreen({
  lang = 'en',
  auth,
}: PlatformBroadcastScreenProps) {
  const [draft, setDraft] = useState<BroadcastDraft>(EMPTY)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const [posted, setPosted] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [version, setVersion] = useState(0)

  const board = useResource<BroadcastView[]>(
    (signal) => getBroadcasts({ ...auth, signal }),
    ['platform-broadcasts', String(version)],
    { isEmpty: (rows) => rows.length === 0 },
  )

  const schools = useResource<SchoolSummary[]>(
    (signal) => getSchools({ ...auth, signal }),
    ['platform-broadcast-schools'],
  )

  const schoolRows = useMemo(
    () => [...(schools.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [schools.data],
  )
  const nameOf = useMemo(() => {
    const map = new Map(schoolRows.map((s) => [s.id, s.name]))
    /* ⚠️ An id with no school renders as itself rather than vanishing — the same
       rule the server keeps for a school archived after the fact. */
    return (id: string) => map.get(id) ?? id
  }, [schoolRows])

  const errors = validateBroadcastDraft(draft)
  const valid = broadcastDraftIsValid(draft)
  const show = (field: keyof typeof errors) => (submitted ? errors[field] : null)

  const edit = (patch: Partial<BroadcastDraft>) => {
    setDraft((d) => ({ ...d, ...patch }))
    setPosted(null)
    setFailed(false)
  }

  const handlePost = () => {
    setSubmitted(true)
    if (!broadcastDraftIsValid(draft)) return

    setBusy(true)
    setFailed(false)
    setPosted(null)
    void createBroadcast(
      {
        title: draft.title.trim(),
        body: draft.body.trim(),
        roles: draft.roles,
        /* 🔴 `[]` IS SENT AS `[]`, WHICH THE SERVER READS AS EVERY SCHOOL. */
        schoolIds: draft.schoolIds,
      },
      auth,
    )
      .then((row) => {
        setPosted(row.title)
        setDraft(EMPTY)
        setSubmitted(false)
        setVersion((n) => n + 1)
      })
      .catch(() => {
        setFailed(true)
      })
      .finally(() => {
        setBusy(false)
      })
  }

  const pull = (id: string) => {
    void withdrawBroadcast(id, auth)
      .catch(() => undefined)
      .finally(() => {
        setVersion((n) => n + 1)
      })
  }

  return (
    <section className="font-dash text-dash-sm text-ink">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* ── Left Column: compose ── */}
        <div className="lg:col-span-7 rounded-3xl border border-line bg-surface p-4 sm:p-6 shadow-card">
          <div className="mb-6 flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-pink-50 text-primary border border-pink-100 shadow-2xs">
              <VoiceIcon size={20} strokeWidth={2.2} />
            </div>
            <div>
              <h2 className="font-dash text-dash-md font-extrabold text-ink">
                Write to the platform
              </h2>
              <p className="mt-1 font-dash text-dash-xs font-medium text-muted leading-relaxed max-w-[65ch]">
                Choose who this is for, then say it. It appears at the top of their dashboard
                the next time they sign in.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-5">
            {/* ── Headline ── */}
            <div>
              <label
                htmlFor="broadcast-title"
                className="mb-1.5 block font-dash text-dash-xs font-bold text-ink"
              >
                Headline
              </label>
              <div className="flex items-center gap-3 rounded-2xl border border-pink-200/90 bg-surface px-3 py-2.5 shadow-2xs transition-all focus-within:border-primary focus-within:ring-2 focus-within:ring-pink-100">
                <input
                  id="broadcast-title"
                  type="text"
                  value={draft.title}
                  maxLength={TITLE_MAX}
                  placeholder="Reports look different from Monday"
                  aria-invalid={show('title') !== null}
                  onChange={(e) => {
                    edit({ title: e.target.value })
                  }}
                  className="w-full bg-transparent font-dash text-dash-sm font-bold text-ink outline-none placeholder:text-muted/60"
                />
                <span className="tabular font-dash text-dash-xs font-bold text-muted/70 shrink-0">
                  {draft.title.length}/{TITLE_MAX}
                </span>
              </div>
              {show('title') ? (
                <p className="mt-1 font-dash text-dash-xs text-muted">{show('title')}</p>
              ) : null}
            </div>

            {/* ── Body ── */}
            <div>
              <label
                htmlFor="broadcast-body"
                className="mb-1.5 block font-dash text-dash-xs font-bold text-ink"
              >
                What you want to say
              </label>
              <div className="flex flex-col rounded-2xl border border-pink-200/90 bg-surface p-3.5 shadow-2xs transition-all focus-within:border-primary focus-within:ring-2 focus-within:ring-pink-100 min-h-[140px]">
                <textarea
                  id="broadcast-body"
                  value={draft.body}
                  rows={4}
                  maxLength={BODY_MAX}
                  placeholder="A level now counts as complete only once, so this month's numbers will look lower than last month's for the same amount of play."
                  aria-invalid={show('body') !== null}
                  onChange={(e) => {
                    edit({ body: e.target.value })
                  }}
                  className="w-full flex-1 bg-transparent font-dash text-dash-sm font-medium text-ink outline-none resize-none placeholder:text-muted/60"
                />
                <div className="flex justify-end pt-1">
                  <span className="tabular font-dash text-dash-xs font-bold text-muted/70">
                    {draft.body.length}/{BODY_MAX}
                  </span>
                </div>
              </div>
              {show('body') ? (
                <p className="mt-1 font-dash text-dash-xs text-muted">{show('body')}</p>
              ) : null}
            </div>

            {/* ── §4: who ── */}
            <fieldset className="rounded-2xl border border-line bg-canvas/40 p-4">
              <legend className="px-1 font-dash text-dash-xs font-bold text-ink">
                Who is this for?
              </legend>
              <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-2">
                {BROADCAST_AUDIENCES.map((audience) => (
                  <CheckboxRow
                    key={audience.role}
                    checked={draft.roles.includes(audience.role)}
                    hint={audience.hint}
                    onChange={() => {
                      edit({ roles: toggleRole(draft.roles, audience.role) })
                    }}
                  >
                    {audience.label}
                  </CheckboxRow>
                ))}
              </div>
              {show('roles') ? (
                <p className="mt-1 font-dash text-dash-xs font-bold text-primary">
                  {show('roles')}
                </p>
              ) : null}
            </fieldset>

            {/* ── §4: "or specific groups" ── */}
            <fieldset className="rounded-2xl border border-line bg-canvas/40 p-4">
              <legend className="px-1 font-dash text-dash-xs font-bold text-ink">
                At which schools?
              </legend>
              {/* 🔴 THE DEFAULT IS EVERY SCHOOL AND IT IS A CHOICE ON THE SCREEN,
                  not an absence. An empty list with no control beside it reads as
                  "I have not chosen yet". */}
              <CheckboxRow
                checked={draft.schoolIds.length === 0}
                hint="Leave this on unless the message is only for particular schools."
                onChange={(everywhere) => {
                  edit({ schoolIds: everywhere ? [] : schoolRows.slice(0, 1).map((s) => s.id) })
                }}
              >
                Every school on the platform
              </CheckboxRow>

              {draft.schoolIds.length > 0 ? (
                <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-line bg-surface p-1">
                  {schoolRows.map((school) => (
                    <CheckboxRow
                      key={school.id}
                      checked={draft.schoolIds.includes(school.id)}
                      onChange={(on) => {
                        edit({
                          schoolIds: on
                            ? [...draft.schoolIds, school.id]
                            : draft.schoolIds.filter((id) => id !== school.id),
                        })
                      }}
                    >
                      {school.name}
                    </CheckboxRow>
                  ))}
                  {schoolRows.length === 0 ? (
                    <p className="px-3 py-2 font-dash text-dash-xs text-muted">
                      {schools.state === 'error'
                        ? 'Couldn’t load the school list.'
                        : 'No schools yet.'}
                    </p>
                  ) : null}
                </div>
              ) : null}
              {show('schoolIds') ? (
                <p className="mt-1 font-dash text-dash-xs font-bold text-primary">
                  {show('schoolIds')}
                </p>
              ) : null}
            </fieldset>

            <Note tone="warning" icon={<AlertIcon size={16} />} detail="Nothing is sent — no message, no WhatsApp, no email. Somebody who never signs in never sees it.">
              This reaches: {describeAudience(draft, nameOf)}
            </Note>
          </div>

          <div className="mt-6 flex flex-col gap-3">
            <div>
              <button
                type="button"
                disabled={busy || !valid}
                onClick={handlePost}
                className="rounded-full bg-primary hover:bg-primary-deep active:scale-98 text-white px-6 py-3 font-dash text-dash-sm font-extrabold shadow-md transition-all flex items-center gap-2.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>{busy ? 'Posting…' : 'Post this broadcast'}</span>
              </button>
            </div>

            {posted !== null && (
              <Note tone="success" icon={<CheckIcon size={16} />}>
                {`“${posted}” is live.`}
              </Note>
            )}
            {failed && (
              <Note tone="danger" icon={<AlertIcon size={16} />}>
                That was not posted. Nothing was sent — try again.
              </Note>
            )}
          </div>
        </div>

        {/* ── Right Column: what has been said ── */}
        <div className="lg:col-span-5 rounded-3xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col">
          <div className="mb-5 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-pink-50 text-primary border border-pink-100">
              <VoiceIcon size={18} strokeWidth={2.2} />
            </div>
            <h2 className="font-dash text-dash-md font-extrabold text-ink">Recent broadcasts</h2>
          </div>

          <DataSurface
            state={board.state}
            skeletonRows={3}
            emptyMessage="Nothing has been broadcast yet."
            errorMessage={describePlatformError(board.error, lang)}
            onRetry={board.retry}
          >
            {board.data === null ? null : <BroadcastList rows={board.data} onWithdraw={pull} />}
          </DataSurface>
        </div>
      </div>
    </section>
  )
}


function BroadcastList({
  rows,
  onWithdraw,
}: {
  rows: readonly BroadcastView[]
  onWithdraw: (id: string) => void
}) {
  const { paginatedItems, page, totalPages, setPage, totalItems } = usePagination(rows, 6)

  return (
    <>
      <div className="flex flex-col gap-3.5">
        {paginatedItems.map((row) => {
          const live = row.publishedAt !== null
          return (
            <div
              key={row.id}
              className="rounded-2xl border border-line bg-surface p-4 shadow-sm transition-all hover:shadow-card flex flex-col gap-2"
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="min-w-0 font-dash text-dash-sm font-extrabold text-ink">
                  {row.title}
                </h3>
                <StatusPill
                  tone={live ? 'success' : 'neutral'}
                  icon={toneIcon(live ? 'success' : 'neutral')}
                >
                  {live ? 'Showing' : 'Withdrawn'}
                </StatusPill>
              </div>

              <p className="font-dash text-dash-xs text-ink-soft whitespace-pre-line">
                {row.body}
              </p>

              {/* 🔴 THE AUDIENCE IS ON THE ROW. "Who was told this" is the only
                  question ever asked about a broadcast afterwards, and a list of
                  headlines with no audience beside them cannot answer it. */}
              <div className="flex flex-wrap items-center gap-1.5">
                {row.roles.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center rounded-full border border-pink-200/90 bg-pink-50 px-2.5 py-0.5 font-dash text-dash-xs font-bold text-primary"
                  >
                    {audienceLabel(role)}
                  </span>
                ))}
                <span className="font-dash text-dash-xs font-semibold text-muted">
                  {row.schoolNames.length === 0
                    ? '• every school'
                    : `• ${row.schoolNames.join(', ')}`}
                </span>
              </div>

              <div className="flex items-center justify-between gap-2">
                <span className="tabular font-dash text-dash-xs text-muted">
                  {new Date(row.createdAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </span>
                {live ? (
                  <button
                    type="button"
                    onClick={() => {
                      onWithdraw(row.id)
                    }}
                    className="font-dash text-dash-xs font-bold text-primary hover:underline cursor-pointer"
                  >
                    Stop showing it
                  </button>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
      <div className="mt-4">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          totalItems={totalItems}
        />
      </div>
    </>
  )
}
